import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { FlexContainer } from "../styled/Container.styled";
import { ActionText } from "../styled/Typography.styled";
import FormItemDatepicker from "./FormItemDatepicker";
import FormItemDropdown from "./FormItemDropdown";
import FormItemInput from "./FormItemInput";
import FormItemRadio from "./FormItemRadio";
import FormItemSlider from "./FormItemSlider";
import { ONE_TIME_PAYMENT } from "../../constants/Payments.constants";

const FormItem = ({
  elementWidth = "auto",
  label = "",
  inputType = "",
  highlightedValue = "",
  value = "",
  setValue = () => {},
  hasAction = false,
  actionLabel = "",
  actionCallback = () => {},
  actionCallbackArguments = [],
  hasComponent = false,
  component = <></>,
  selectedType = ONE_TIME_PAYMENT,
  datepickerOpen = false,
  setDatepickerOpen = () => {},
  minDate = "",
  maxDate = "",
  filterDate = () => {
    return true;
  },
  excludedDates = [],
  disabledOptions = [],
  dropdownOptions = [],
  dropdownKey = "",
  dropdownValue = "",
  dropdownLabel = "",
  inputPlaceholder = "",
  blurHandler = () => {},
  blurOnWheel = false,
  adornment = false,
  adornmentPosition = "",
  adornmentSymbol = "",
  radioOptions = [],
  inputProps = {},
  disabled = false,
  errorMessage = "",
  labelTextAlign,
  labelWidth,
  gap = "25px",
}) => {
  const [inputRef, setInputRef] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const resolvedLabelTextAlign =
    labelTextAlign ?? (isMobile ? "start" : "end");
  const resolvedLabelWidth = labelWidth ?? elementWidth;

  useEffect(() => {
    if (inputRef?.current && actionCallbackArguments[0] !== inputRef?.current) {
      actionCallbackArguments.unshift(inputRef.current);
    }
  }, [inputRef, actionCallbackArguments]);

  const renderInputItem = () => {
    switch (inputType) {
      case "input":
        return (
          <FormItemInput
            elementWidth={isMobile ? "100%" : elementWidth}
            setRef={setInputRef}
            value={value}
            setValue={setValue}
            placeholder={inputPlaceholder}
            blurHandler={blurHandler}
            blurOnWheel={blurOnWheel}
            adornment={adornment}
            adornmentPosition={adornmentPosition}
            adornmentSymbol={adornmentSymbol}
            inputProps={inputProps}
            disabled={disabled}
            errorMessage={errorMessage}
          />
        );
      case "dropdown":
        return (
          <FormItemDropdown
            elementWidth={isMobile ? "100%" : elementWidth}
            highlightedValue={highlightedValue}
            value={value}
            setValue={setValue}
            disabledOptions={disabledOptions}
            options={dropdownOptions}
            keyAttribute={dropdownKey}
            valueAttribute={dropdownValue}
            labelAttribute={dropdownLabel}
            disabled={disabled}
            errorMessage={errorMessage}
          />
        );
      case "datepicker":
        return (
          <FormItemDatepicker
            elementWidth={isMobile ? "100%" : elementWidth}
            startDate={value}
            setStartDate={setValue}
            datepickerOpen={datepickerOpen}
            setDatepickerOpen={setDatepickerOpen}
            minDate={minDate}
            maxDate={maxDate}
            filterDate={filterDate}
            excludedDates={excludedDates}
          />
        );
      case "radio":
        return (
          <FormItemRadio
            elementWidth={isMobile ? "100%" : elementWidth}
            options={radioOptions}
            value={value}
            setValue={setValue}
          />
        );
      case "slider":
        return (
          <FormItemSlider
            label={label}
            value={value}
            setValue={setValue}
            min={inputProps.min}
            max={inputProps.max}
            step={inputProps.step}
            marks={inputProps.marks}
            valueLabelDisplay={inputProps.valueLabelDisplay}
            disabled={disabled}
            errorMessage={errorMessage}
            {...inputProps}
          />
        );
      default:
        return <Box minWidth={isMobile ? "100%" : elementWidth} />;
    }
  };

  return (
    <FlexContainer 
      gap={gap} 
      width="100%" 
      flexdirection={isMobile ? "column" : "row"}
      alignitems={isMobile ? "flex-start" : "center"}
    >
      <Typography 
        fontWeight="bold" 
        width={isMobile ? "100%" : resolvedLabelWidth} 
        textAlign={resolvedLabelTextAlign}
      >
        {label}
      </Typography>
      {renderInputItem()}
      {hasAction ? (
        <ActionText>
          <Typography
            onClick={(e) => actionCallback(...actionCallbackArguments)}
            minWidth={isMobile ? "100%" : elementWidth}
          >
            {actionLabel}
          </Typography>
        </ActionText>
      ) : hasComponent ? (
        component
      ) : (
        <Box minWidth={isMobile ? "100%" : elementWidth} />
      )}
    </FlexContainer>
  );
};

export default FormItem;